import type { Resources } from '@/types/translations-resources'
import { buildPathDerivedResources } from '@/utils/i18n-to-migrate/discovery-shared.mts'

// Recursively flattens a translation resource object into dotted leaf paths,
// e.g. { additionalInformation: { title: string } } -> 'additionalInformation.title'.
// Every .no.json leaf value is a string (verified: no arrays, no non-string leaves),
// so recursion bottoms out as soon as a string value is reached.
type FlattenKeys<T> = T extends string
  ? never
  : {
      [K in keyof T & string]: T[K] extends string ? K : `${K}.${FlattenKeys<T[K]>}`
    }[keyof T & string]

type Section = keyof Resources
type TranslationPath = {
  [S in Section]: `${S & string}.${FlattenKeys<Resources[S]>}`
}[Section]

// Accepts a validated TranslationPath for literal keys, while still allowing
// genuinely dynamic (runtime-computed, e.g. template-literal) keys through as
// plain `string`. Plain `string & {}` would defeat this: every string literal
// is structurally assignable to `string & {}`, so the union would silently
// accept any literal regardless of TranslationPath — this conditional type
// only widens to `string` when the caller's inferred type K is itself as wide
// as `string` (i.e. not a literal), otherwise it enforces K extends TranslationPath.
type TranslationKey<K extends string> = string extends K
  ? string
  : K extends TranslationPath
    ? K
    : TranslationPath

type TranslationRecord = Record<string, Record<string, unknown> | undefined>

const noModules = import.meta.glob<Record<string, unknown>>('/src/**/*.no.json', {
  eager: true,
  import: 'default',
})

const resources = buildPathDerivedResources(Object.entries(noModules)) as TranslationRecord

function resolve<K extends string>(
  key: TranslationKey<K>,
  params?: Record<string, string | number>,
): string {
  const dotIndex = (key as string).indexOf('.')

  if (dotIndex === -1) {
    if (import.meta.env.DEV) console.warn(`Translation not found for key: "${key}"`)
    return key
  }

  const section = (key as string).slice(0, dotIndex)
  const rest = (key as string).slice(dotIndex + 1)

  const sectionObj = resources[section]

  if (!sectionObj) {
    if (import.meta.env.DEV) console.warn(`Translation section not found for key: "${key}"`)
    return key
  }

  const value = rest
    .split('.')
    .reduce<unknown>(
      (obj, k) =>
        obj && typeof obj === 'object' ? (obj as Record<string, unknown>)[k] : undefined,
      sectionObj,
    )

  if (typeof value !== 'string') {
    if (import.meta.env.DEV) console.warn(`Translation not found for key: "${key}"`)
    return key
  }

  if (params) {
    return Object.entries(params).reduce(
      (str, [k, v]) => str.split(`{{${k}}}`).join(String(v)),
      value,
    )
  }

  return value
}

export function useTranslations(): {
  t: <K extends string>(key: TranslationKey<K>, params?: Record<string, string | number>) => string
} {
  return { t: resolve }
}

// Standalone t — works outside React components, same resolution logic as useTranslations.
export const t = resolve
